import { useState, useCallback, useMemo } from 'react';

// Constants
const MAX_CONTEXTS = 10;

/**
 * Hook to manage recent context for easy re-quoting (session-only, no persistence)
 * @returns {Object} Recent context management functions and state
 */
export const useRecentContext = () => {
  const [recentContexts, setRecentContexts] = useState([]);

  const [isPanelVisible, setIsPanelVisible] = useState(false);


  // Add a new context when a message is quoted
  const addContext = useCallback((message) => {
    if (!message || !message.message) return;

    const contextId = `context_${message.id}_${Date.now()}`;
    const newContext = {
      id: contextId,
      originalMessageId: message.id,
      content: typeof message.message === 'string' ? message.message : message.message.message || JSON.stringify(message.message),
      timestamp: new Date().toISOString(),
      quoteCount: 1,
      sender: message.sender
    };

    setRecentContexts(prev => {
      // Check if this exact content already exists
      const existingIndex = prev.findIndex(ctx => 
        ctx.content === newContext.content && 
        ctx.sender === newContext.sender
      );

      let updatedContexts;
      if (existingIndex >= 0) {
        // Update existing context
        updatedContexts = [...prev];
        updatedContexts[existingIndex] = {
          ...updatedContexts[existingIndex],
          timestamp: new Date().toISOString(),
          quoteCount: updatedContexts[existingIndex].quoteCount + 1
        };
      } else {
        // Add new context
        updatedContexts = [newContext, ...prev];
      }

      // Keep only the most recent contexts
      const limitedContexts = updatedContexts.slice(0, MAX_CONTEXTS);
      return limitedContexts;
    });
  }, []);

  // Remove a context
  const removeContext = useCallback((contextId) => {
    setRecentContexts(prev => prev.filter(ctx => ctx.id !== contextId));
  }, []);

  // Clear all contexts
  const clearAllContexts = useCallback(() => {
    setRecentContexts([]);
  }, []);

  // Toggle panel visibility
  const togglePanel = useCallback(() => {
    setIsPanelVisible(prev => !prev);
  }, []);

  // Show panel
  const showPanel = useCallback(() => {
    setIsPanelVisible(true);
  }, []);

  // Hide panel
  const hidePanel = useCallback(() => {
    setIsPanelVisible(false);
  }, []);


  // Memoized context for quoting (converts back to message format)
  const createQuoteContext = useCallback((context) => {
    return {
      id: context.originalMessageId,
      message: context.content,
      sender: context.sender,
      timestamp: context.timestamp
    };
  }, []);

  // Memoized stats
  const contextStats = useMemo(() => ({
    totalContexts: recentContexts.length,
    mostQuoted: recentContexts.reduce((max, ctx) => 
      ctx.quoteCount > max.quoteCount ? ctx : max, 
      { quoteCount: 0 }
    ),
    recentCount: recentContexts.filter(ctx => {
      const hoursDiff = (new Date() - new Date(ctx.timestamp)) / (1000 * 60 * 60);
      return hoursDiff <= 24;
    }).length
  }), [recentContexts]);

  return {
    recentContexts,
    isPanelVisible,
    contextStats,
    addContext,
    removeContext,
    clearAllContexts,
    togglePanel,
    showPanel,
    hidePanel,
    createQuoteContext
  };
};

export default useRecentContext;
